import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionAPI } from '../services/api';
import {
  ArrowLeft, Plus, DollarSign, Calendar,
  Trash2, Download, User, Building
} from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';
import Button from '../components/Button';
import * as XLSX from 'xlsx';
import NumberInput from '../components/NumberInput';

const ContractCosts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  const [showAddForm, setShowAddForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  /** 'pay' | 'edit' — 수정은 납부완료·부분납부 건의 납부방법·납부일만 변경 */
  const [paymentModalMode, setPaymentModalMode] = useState('pay');
  const [selectedCost, setSelectedCost] = useState(null);
  const [paymentData, setPaymentData] = useState({
    payment_method: '',
    payment_date: '',
    status: '납부완료',
    description: ''
  });
  const [newCost, setNewCost] = useState({
    type: '',
    amount: 0,
    due_date: '',
    description: '',
    status: '미납'
  });

  // 계약 정보 조회
  const { data: contract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => transactionAPI.getById(id),
    select: (response) => response.data,
  });

  // 비용 내역 조회
  const { data: costsData, isLoading: isLoadingCosts } = useQuery({
    queryKey: ['contract-costs', id],
    queryFn: () => transactionAPI.getCosts(id),
    select: (response) => response.data?.data || {},
  });

  // 기본 비용 납부 상태 업데이트 mutation
  const updateBasicCostStatusMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateBasicCostStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('기본 비용 납부 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError(error.response?.data?.message || '기본 비용 납부 상태 업데이트에 실패했습니다.');
    }
  });

  // 석물 비용 납부 상태 업데이트 mutation
  const updateStoneCostStatusMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateStoneCostStatus(id, data.stoneId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('석물 비용 납부 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError(error.response?.data?.message || '석물 비용 납부 상태 업데이트에 실패했습니다.');
    }
  });

  // 화병 비용 납부 상태 업데이트 mutation
  const updateVaseCostStatusMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateVaseCostStatus(id, data.vaseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('화병 비용 납부 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError(error.response?.data?.message || '화병 비용 납부 상태 업데이트에 실패했습니다.');
    }
  });

  // 각자비 납부 상태 업데이트 mutation
  const updateEngravingCostStatusMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateEngravingCostStatus(id, data.engravingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('각자비 납부 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError(error.response?.data?.message || '각자비 납부 상태 업데이트에 실패했습니다.');
    }
  });

  // 회차별 cost_items(사용료 2 등) 납부 상태 업데이트
  const updateCostItemStatusMutation = useMutation({
    mutationFn: (data) => transactionAPI.updateCostItemStatus(id, data.costItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('비용 항목 납부 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError(error.response?.data?.message || '비용 항목 납부 상태 업데이트에 실패했습니다.');
    }
  });

  // 비용 데이터 통합 (기본 비용, 회차별 cost_items, 추가 비용, 석물 비용 포함)
  const costs = [
    ...(costsData?.costs || []),
    // 기본 비용 (costs 행 — 최초 계약 금액만, 안치자 추가분은 cost_items에 표시)
    ...(costsData?.cost ? [
      {
        id: 'usage_fee',
        cost_type: '사용료',
        amount: costsData.cost.usage_fee || 0,
        description: '기본 사용료',
        note: costsData.cost.memo || '',
        status: costsData.cost.usage_fee_status || '미납',
        payment_method: costsData.cost.usage_fee_payment_method,
        payment_date: costsData.cost.usage_fee_payment_date,
        created_at: new Date().toISOString(),
        is_basic_cost: true,
        cost_field: 'usage_fee'
      },
      {
        id: 'management_fee',
        cost_type: '관리비',
        amount: costsData.cost.management_fee || 0,
        description: '기본 관리비',
        note: costsData.cost.memo || '',
        status: costsData.cost.management_fee_status || '미납',
        payment_method: costsData.cost.management_fee_payment_method,
        payment_date: costsData.cost.management_fee_payment_date,
        created_at: new Date().toISOString(),
        is_basic_cost: true,
        cost_field: 'management_fee'
      },
      {
        id: 'burial_work_fee',
        cost_type: '매장작업비',
        amount: costsData.cost.burial_work_fee || 0,
        description: '매장 작업 비용',
        note: costsData.cost.memo || '',
        status: costsData.cost.burial_work_fee_status || '미납',
        payment_method: costsData.cost.burial_work_fee_payment_method,
        payment_date: costsData.cost.burial_work_fee_payment_date,
        created_at: new Date().toISOString(),
        is_basic_cost: true,
        cost_field: 'burial_work_fee'
      },
      {
        id: 'installation_fee',
        cost_type: '설치비',
        amount: costsData.cost.installation_fee || 0,
        description: '설치 비용',
        note: costsData.cost.memo || '',
        status: costsData.cost.installation_fee_status || '미납',
        payment_method: costsData.cost.installation_fee_payment_method,
        payment_date: costsData.cost.installation_fee_payment_date,
        created_at: new Date().toISOString(),
        is_basic_cost: true,
        cost_field: 'installation_fee'
      }
    ] : []),
    ...(costsData?.cost_items || []).map((item) => ({
      id: `costitem-${item.id}`,
      cost_type: item.cost_name,
      amount: item.fee,
      description: '안치자 추가 등 회차별 비용',
      note: item.memo || '',
      status: item.fee_status === '납부' ? '납부완료' : (item.fee_status === '부분납부' ? '부분납부' : (item.fee_status || '미납')),
      payment_method: item.payment_method,
      payment_date: item.payment_date,
      created_at: item.created_at,
      is_cost_item: true
    })),
    ...(costsData?.additional_costs || []).map((c) => ({
      ...c,
      note: c.description || ''
    })),
    // 석물 비용 추가
    ...(costsData?.stone_objects?.map((stone, index) => ({
      id: `stone-${stone.id}`,
      cost_type: `석물 ${index + 1}`,
      amount: stone.cost || 0,
      description: `${stone.stone_type || ''}${stone.size ? ` (${stone.size})` : ''}`.trim() || '석물',
      note: stone.stone_memo || '',
      status: stone.payment_status || '미납',
      payment_method: stone.payment_method,
      payment_date: stone.payment_date,
      created_at: new Date().toISOString(),
      is_stone: true
    })) || []),
    ...(costsData?.engravings || []).map((eng, index) => {
      const typeLabel = eng.engraving_type === 'initial' ? '각자비' : (eng.engraving_type || '각자');
      const descParts = [typeLabel, eng.content].filter(Boolean);
      return {
        id: `engraving-${eng.id}`,
        cost_type: `각자 ${index + 1}`,
        amount: eng.total_cost || 0,
        description: descParts.join(' — ') || '각자비',
        note: eng.memo || '',
        status: eng.fee_status === '납부' ? '납부완료' : (eng.fee_status === '부분납부' ? '부분납부' : (eng.fee_status || '미납')),
        payment_method: eng.payment_method,
        payment_date: eng.payment_date,
        created_at: eng.created_at || new Date().toISOString(),
        is_engraving: true
      };
    }),
    // 화병 비용 추가
    ...(costsData?.stone_objects?.flatMap((stone, stoneIndex) =>
      (stone.vases || []).map((vase, vaseIndex) => ({
        id: `vase-${vase.id}`,
        cost_type: `화병 ${stoneIndex + 1}-${vaseIndex + 1}`,
        amount: vase.cost || 0,
        description: `화병 (${vase.size})`,
        note: vase.memo || '',
        status: vase.payment_status || '미납',
        payment_method: vase.payment_method,
        payment_date: vase.payment_date,
        created_at: new Date().toISOString(),
        is_vase: true
      }))
    ) || [])
  ];

  // 비용 추가 뮤테이션
  const addCostMutation = useMutation({
    mutationFn: (costData) => transactionAPI.addCost(id, costData),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('비용이 추가되었습니다.');
      setShowAddForm(false);
      setNewCost({
        type: '',
        amount: 0,
        due_date: '',
        description: '',
        status: '미납'
      });
    },
    onError: (error) => {
      showError('비용 추가에 실패했습니다.');
      console.error('비용 추가 실패:', error);
    }
  });

  // 비용 상태 업데이트 뮤테이션
  const updateCostStatusMutation = useMutation({
    mutationFn: ({ costId, status, payment_method, payment_date, description }) =>
      transactionAPI.updateCostStatus(id, costId, {
        status,
        ...(payment_method != null ? { payment_method } : {}),
        ...(payment_date != null ? { payment_date } : {}),
        ...(description !== undefined ? { description } : {})
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('비용 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError('비용 상태 업데이트에 실패했습니다.');
      console.error('비용 상태 업데이트 실패:', error);
    }
  });

  // 비용 삭제 뮤테이션
  const deleteCostMutation = useMutation({
    mutationFn: (costId) => transactionAPI.deleteCost(id, costId),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-costs', id]);
      success('비용이 삭제되었습니다.');
    },
    onError: (error) => {
      showError('비용 삭제에 실패했습니다.');
      console.error('비용 삭제 실패:', error);
    }
  });



  // 권한 확인
  if (!hasPermission(user?.role, 'COST_MANAGE')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <DollarSign size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">비용 관리 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  const handleAddCost = () => {
    if (!newCost.type || !newCost.amount || !newCost.due_date) {
      showError('필수 항목을 모두 입력해주세요.');
      return;
    }
    addCostMutation.mutate(newCost);
  };

  const handleStatusChange = (costId, status) => {
    // 석물·화병·각자비는 납부처리 버튼으로 처리
    if (costId.startsWith('stone-') || costId.startsWith('vase-') || costId.startsWith('engraving-')) {
      showError('석물·화병·각자비 납부는 목록의 납부처리 버튼을 이용해주세요.');
      return;
    }

    // 기본 비용인 경우 처리
    if (costId === 'usage_fee' || costId === 'management_fee' || costId === 'burial_work_fee' || costId === 'installation_fee') {
      updateBasicCostStatusMutation.mutate({ costType: costId, status });
      return;
    }

    updateCostStatusMutation.mutate({ costId, status });
  };

  const handleDeleteCost = (costId) => {
    if (window.confirm('정말로 이 비용을 삭제하시겠습니까?')) {
      deleteCostMutation.mutate(costId);
    }
  };

  const handleBasicCostStatusChange = (costType, status) => {
    updateBasicCostStatusMutation.mutate({ costType, status });
  };

  const toDateInputValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handlePaymentModalOpen = (cost) => {
    setPaymentModalMode('pay');
    setSelectedCost(cost);
    setPaymentData({
      payment_method: '',
      payment_date: new Date().toISOString().split('T')[0],
      status: '납부완료',
      description: cost.note != null ? String(cost.note) : ''
    });
    setShowPaymentModal(true);
  };

  const handlePaymentEditOpen = (cost) => {
    setPaymentModalMode('edit');
    setSelectedCost(cost);
    setPaymentData({
      payment_method: cost.payment_method || '',
      payment_date: toDateInputValue(cost.payment_date) || new Date().toISOString().split('T')[0],
      status: cost.status || '미납',
      description: cost.note != null ? String(cost.note) : ''
    });
    setShowPaymentModal(true);
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    setSelectedCost(null);
    setPaymentModalMode('pay');
    setPaymentData({
      payment_method: '',
      payment_date: '',
      status: '납부완료',
      description: ''
    });
  };

  const handlePaymentSubmit = () => {
    const status = paymentData.status;
    if (status !== '미납' && (!paymentData.payment_method || !paymentData.payment_date)) {
      showError('미납이 아닌 경우 납부방법과 납부일을 모두 입력해주세요.');
      return;
    }

    const payloadBase = {
      status,
      description: paymentData.description
    };
    const paidPayload =
      status === '미납'
        ? {}
        : {
            payment_method: paymentData.payment_method,
            payment_date: paymentData.payment_date
          };

    if (selectedCost.is_basic_cost) {
      updateBasicCostStatusMutation.mutate({
        costType: selectedCost.id,
        ...payloadBase,
        ...paidPayload
      });
    } else if (selectedCost.is_stone) {
      const stoneId = selectedCost.id.replace('stone-', '');
      updateStoneCostStatusMutation.mutate({
        stoneId,
        ...payloadBase,
        ...paidPayload
      });
    } else if (selectedCost.is_vase) {
      const vaseId = selectedCost.id.replace('vase-', '');
      updateVaseCostStatusMutation.mutate({
        vaseId,
        ...payloadBase,
        ...paidPayload
      });
    } else if (selectedCost.is_engraving) {
      const engravingId = selectedCost.id.replace('engraving-', '');
      updateEngravingCostStatusMutation.mutate({
        engravingId,
        ...payloadBase,
        ...paidPayload
      });
    } else if (selectedCost.is_cost_item) {
      const costItemId = String(selectedCost.id).replace(/^costitem-/, '');
      updateCostItemStatusMutation.mutate({
        costItemId,
        ...payloadBase,
        ...paidPayload
      });
    } else {
      updateCostStatusMutation.mutate({
        costId: selectedCost.id,
        ...payloadBase,
        ...paidPayload
      });
    }

    handlePaymentModalClose();
  };

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!costs || costs.length === 0) {
      showError('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      const excelData = costs.map(cost => ({
        '비용종류': cost.cost_type || cost.type,
        '금액': formatCurrency(cost.amount),
        '납부기한': formatDate(cost.due_date),
        '납부상태': cost.status,
        '설명': cost.note != null && cost.note !== '' ? cost.note : (cost.description || ''),
        '등록일': formatDate(cost.created_at)
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '비용내역');

      const fileName = `계약${id}_비용내역_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showError('엑셀 다운로드에 실패했습니다.');
    }
  };

  // 권한 확인
  if (!hasPermission(user?.role, 'COST_MANAGE')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <DollarSign size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">비용 관리 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (isLoadingContract || isLoadingCosts) {
    return <div className="loading">비용 정보를 불러오는 중...</div>;
  }

  if (!contract) {
    return (
      <div className="error">
        계약 정보를 찾을 수 없습니다.
      </div>
    );
  }

  // 통계 계산
  const basicCostTotal = costsData?.cost ?
    (parseFloat(costsData.cost.usage_fee) || 0) +
    (parseFloat(costsData.cost.burial_work_fee) || 0) +
    (parseFloat(costsData.cost.installation_fee) || 0) : 0;

  // 기본 비용 납부완료 금액 계산
  const basicCostPaid = costsData?.cost ?
    ((costsData.cost.usage_fee_status === '납부완료' || costsData.cost.usage_fee_status === '부분납부') ? parseFloat(costsData.cost.usage_fee) || 0 : 0) +
    ((costsData.cost.burial_work_fee_status === '납부완료' || costsData.cost.burial_work_fee_status === '부분납부') ? parseFloat(costsData.cost.burial_work_fee) || 0 : 0) +
    ((costsData.cost.installation_fee_status === '납부완료' || costsData.cost.installation_fee_status === '부분납부') ? parseFloat(costsData.cost.installation_fee) || 0 : 0) : 0;

  // 추가 비용, 석물 비용, 화병 비용만 계산 (기본 비용 제외)
  const otherCostsTotal = costs?.reduce((sum, cost) => {
    if (cost.is_basic_cost) return sum; // 기본 비용은 제외
    return sum + (parseFloat(cost.amount) || 0);
  }, 0) || 0;

  const otherCostsPaid = costs?.reduce((sum, cost) => {
    if (cost.is_basic_cost) return sum; // 기본 비용은 제외
    return (cost.status === '납부완료' || cost.status === '부분납부') ? sum + (parseFloat(cost.amount) || 0) : sum;
  }, 0) || 0;

  const totalAmount = basicCostTotal + otherCostsTotal;
  const paidAmount = basicCostPaid + otherCostsPaid;
  const unpaidAmount = totalAmount - paidAmount;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate(`/contracts/${id}`)}
            variant="outline"
            icon={ArrowLeft}
          >
            계약 상세로
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">비용 상세</h1>
            {/* <p className="text-gray-600">계약번호: {contract.id}</p> */}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {hasPermission(user?.role, 'COST_CREATE') && (
            <Button
              onClick={() => setShowAddForm(true)}
              variant="primary"
              icon={Plus}
            >
              비용 추가
            </Button>
          )}
          {costs && costs.length > 0 && (
            <Button
              onClick={handleExcelDownload}
              variant="outline"
              icon={Download}
            >
              엑셀 다운로드
            </Button>
          )}
        </div>
      </div>

      {/* 계약 정보 요약 */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">계약 정보</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <User size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">사망자:</span>
              <span className="font-medium">{contract.deads?.[0]?.name || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Building size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">묘역:</span>
              <span className="font-medium">
                {contract.mausoleum ?
                  `${contract.mausoleum.complex}-${contract.mausoleum.area}-${contract.mausoleum.row_number}-${contract.mausoleum.number}` :
                  '-'
                }
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">계약일:</span>
              <span className="font-medium">{formatDate(contract.contract_date)}</span>
            </div>
          </div>
        </div>
      </div>



      {/* 비용 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-blue-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(totalAmount)}
            </div>
            <div className="text-sm text-gray-600">총 비용</div>
          </div>
        </div>
        <div className="card bg-green-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(paidAmount)}
            </div>
            <div className="text-sm text-gray-600">납부 완료</div>
          </div>
        </div>
        <div className="card bg-red-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(unpaidAmount)}
            </div>
            <div className="text-sm text-gray-600">미납 금액</div>
          </div>
        </div>
      </div>

      {/* 비용 추가 폼 */}
      {showAddForm && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">비용 추가</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="form-label">비용 종류</label>
                <select
                  value={newCost.type}
                  onChange={(e) => setNewCost(prev => ({ ...prev, type: e.target.value }))}
                  className="select"
                >
                  <option value="">선택하세요</option>
                  <option value="사용료">사용료</option>
                  <option value="관리비">관리비</option>
                  <option value="매장작업비">매장작업비</option>
                  <option value="설치비">설치비</option>
                  <option value="석물비">석물비</option>
                  <option value="각자비">각자비</option>
                  <option value="재사용료">재사용료</option>
                  <option value="보수비">보수비</option>
                  <option value="기타">기타</option>
                </select>
              </div>
              <div>
                <label className="form-label">금액</label>
                <NumberInput
                  value={newCost.amount}
                  onChange={(val) => setNewCost(prev => ({ ...prev, amount: val }))}
                  className="input"
                  placeholder="금액을 입력하세요"
                />
              </div>
              <div>
                <label className="form-label">납부기한</label>
                <input
                  type="date"
                  value={newCost.due_date}
                  onChange={(e) => setNewCost(prev => ({ ...prev, due_date: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="form-label">납부상태</label>
                <select
                  value={newCost.status}
                  onChange={(e) => setNewCost(prev => ({ ...prev, status: e.target.value }))}
                  className="select"
                >
                  <option value="미납">미납</option>
                  <option value="납부완료">납부완료</option>
                  <option value="부분납부">부분납부</option>
                </select>
              </div>
              <div className="md:col-span-2 lg:col-span-4">
                <label className="form-label">설명</label>
                <textarea
                  value={newCost.description}
                  onChange={(e) => setNewCost(prev => ({ ...prev, description: e.target.value }))}
                  className="input"
                  rows="3"
                  placeholder="비용에 대한 설명을 입력하세요"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <Button
                onClick={() => setShowAddForm(false)}
                variant="outline"
              >
                취소
              </Button>
              <Button
                onClick={handleAddCost}
                disabled={addCostMutation.isLoading}
                loading={addCostMutation.isLoading}
                variant="primary"
              >
                추가
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 추가 비용 정보 */}
      {costsData?.additional_costs && costsData.additional_costs.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold">추가 비용 정보</h2>
          </div>
          <div className="card-body">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>비용 종류</th>
                    <th className="text-left">금액</th>
                    <th>설명</th>
                    <th>등록일</th>
                  </tr>
                </thead>
                <tbody>
                  {costsData.additional_costs.map((cost) => (
                    <tr key={cost.id}>
                      <td className="font-medium">{cost.cost_type}</td>
                      <td className="font-medium text-left">
                        {formatCurrency(cost.amount)}
                      </td>
                      <td className="max-w-xs truncate" title={cost.description}>
                        {cost.description || '-'}
                      </td>
                      <td>{formatDate(cost.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}



      {/* 비용 목록 */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">비용 내역</h2>
        </div>
        <div className="card-body">
          {costs && costs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>비용종류</th>
                    <th className="text-left">금액</th>
                    <th>설명</th>
                    <th>납부상태</th>
                    <th>납부방법</th>
                    <th>납부일</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.map((cost) => (
                    <tr key={cost.id}>
                      <td className="font-medium">{cost.cost_type || cost.type}</td>
                      <td className="font-medium text-left">
                        {formatCurrency(cost.amount)}
                      </td>
                      <td className="max-w-xs text-sm text-gray-700 whitespace-pre-wrap break-words" title={cost.note || cost.description || ''}>
                        {(cost.note != null && String(cost.note).trim() !== '') ? cost.note : (cost.description || '—')}
                      </td>
                      <td>
                        <span className={`text-sm px-2 py-1 rounded ${cost.status === '납부완료' ? 'bg-green-100 text-green-800' :
                            cost.status === '부분납부' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                          }`}>
                          {cost.status}
                        </span>
                      </td>
                      <td>
                        {cost.payment_method || '-'}
                      </td>
                      <td>
                        {cost.payment_date ? formatDate(cost.payment_date) : '-'}
                      </td>
                      <td>
                        <div className="flex items-center space-x-2 flex-wrap gap-1">
                          {cost.status !== '납부완료' && hasPermission(user?.role, 'COST_EDIT') && (
                            <button
                              onClick={() => handlePaymentModalOpen(cost)}
                              className="btn btn-primary btn-sm"
                              title="납부처리"
                            >
                              납부처리
                            </button>
                          )}
                          {hasPermission(user?.role, 'COST_EDIT') && (
                            <button
                              type="button"
                              onClick={() => handlePaymentEditOpen(cost)}
                              className="btn btn-outline btn-sm"
                              title="상태·납부정보·설명 수정"
                            >
                              수정
                            </button>
                          )}
                          {hasPermission(user?.role, 'COST_DELETE') && !cost.is_stone && !cost.is_vase && !cost.is_engraving && !cost.is_basic_cost && !cost.is_cost_item && (
                            <button
                              onClick={() => handleDeleteCost(cost.id)}
                              className="text-red-600 hover:text-red-800"
                              title="삭제"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <DollarSign size={48} className="mx-auto mb-4 text-gray-300" />
              <p>등록된 비용 내역이 없습니다.</p>
            </div>
          )}
        </div>
      </div>

      {/* 석물 비용 정보
      {costsData?.stone_objects && costsData.stone_objects.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold">석물 비용 정보</h2>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              {costsData.stone_objects.map((stone, index) => (
                <div key={stone.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-md font-medium">석물 {index + 1}</h3>
                    <span className="text-lg font-bold text-blue-600">
                      {formatCurrency(stone.cost || 0)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">석물 종류:</span>
                      <span className="ml-2 font-medium">{stone.stone_type || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">크기:</span>
                      <span className="ml-2 font-medium">{stone.size || '-'}</span>
                    </div>
                    {stone.stone_memo && (
                      <div className="md:col-span-2">
                        <span className="text-gray-600">메모:</span>
                        <span className="ml-2">{stone.stone_memo}</span>
                      </div>
                    )}
                  </div>
                  {stone.vases && stone.vases.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">화병 정보</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {stone.vases.map((vase, vaseIndex) => (
                          <div key={vase.id} className="text-sm">
                            <span className="text-gray-600">화병 {vaseIndex + 1}:</span>
                            <span className="ml-1 font-medium">{vase.size}</span>
                            <span className="ml-2 text-blue-600">
                              {formatCurrency(vase.cost || 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )} */}

      {/* 납부처리 모달 */}
      {showPaymentModal && selectedCost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {paymentModalMode === 'edit' ? '납부정보 수정' : '납부처리'}
              </h3>
              <button
                onClick={handlePaymentModalClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">비용 정보</p>
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium">{selectedCost.cost_type || selectedCost.type}</p>
                <p className="text-lg font-bold text-blue-600">
                  {formatCurrency(selectedCost.amount)}원
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  납부 상태 *
                </label>
                <select
                  value={paymentData.status}
                  onChange={(e) => setPaymentData((prev) => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="미납">미납</option>
                  <option value="부분납부">부분납부</option>
                  <option value="납부완료">납부완료</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  설명
                </label>
                <textarea
                  value={paymentData.description}
                  onChange={(e) => setPaymentData((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="비고·설명을 입력하세요"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  납부방법 {paymentData.status !== '미납' ? '*' : ''}
                </label>
                <input
                  type="text"
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_method: e.target.value }))}
                  placeholder="납부방법을 입력하세요 (예: 현금, 카드, 계좌이체 등)"
                  disabled={paymentData.status === '미납'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  납부일 {paymentData.status !== '미납' ? '*' : ''}
                </label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                  disabled={paymentData.status === '미납'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handlePaymentModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handlePaymentSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {paymentModalMode === 'edit' ? '저장' : '납부처리'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractCosts; 